import { useRuntimeConfig } from '#imports'
import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import type { Price } from '@backmarket/http-api'
import type {
  PaymentMethod,
  PaymentMethodCode,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import {
  getDuration,
  isDurationKey,
} from '../../../utils/performance/getDuration'
import type { PaymentLogTypes } from '../../form-common/types'
import { isPaymentMethodEnabled } from '../../methods/helpers/methods'
import { PaymentMarkName } from '../../pages/performance/constant'

// Events that need to be tracked with Noodle
const NOODLE_EVENTS: PaymentLogTypes[] = [
  'PAYMENT_FORM_DISPLAYED',
  'PAYMENT_FORM_METHOD_SELECTED',
  'PAYMENT_FORM_SUBMIT_START',
  'PAYMENT_FORM_SUBMIT_SUCCESS',
  'PAYMENT_FORM_SUBMIT_ERROR',
]

export type TrackingData = {
  basePrice: MaybeRefOrGetter<Price>
  selectedPaymentMethod: MaybeRefOrGetter<PaymentMethod | null>
  availablePaymentMethods?: MaybeRefOrGetter<PaymentMethod[]>
  defaultPaymentMethod?: MaybeRefOrGetter<PaymentMethod | null>
  isPaymentRequired?: MaybeRefOrGetter<boolean>
  isExpressCheckout?: MaybeRefOrGetter<boolean>
}

export const usePaymentFormEventTracker = (
  reactiveOptions: MaybeRefOrGetter<TrackingData>,
) => {
  const { user } = useUserStore()
  // Note: clientId field in payload should contain userId value for BigQuery matching
  // This maintains backward compatibility with existing queries that expect USER_ID in CLIENT_ID field
  const clientId = computed(() => user.value.userId)
  const logger = useLogger()
  const {
    public: { FF_NOODLE_PAYMENT_EVENTS_ENABLED },
  } = useRuntimeConfig()
  const { trackPaymentEvent } = useTracking()
  const trackBuilder =
    (type: PaymentLogTypes, message: string) =>
    (data?: Record<string, unknown>) => {
      const options = toValue(reactiveOptions)
      const basePrice = toValue(options.basePrice)
      const defaultPaymentMethod = toValue(options.defaultPaymentMethod)
      const selectedPaymentMethod = toValue(options.selectedPaymentMethod)
      const availablePaymentMethods = toValue(options.availablePaymentMethods)
      const required = toValue(options.isPaymentRequired)
      const isExpressCheckout = toValue(options.isExpressCheckout)

      const duration = isDurationKey(type) ? getDuration(type) : null

      const payload = {
        clientId: clientId.value,
        payment: {
          amountAfterDiscount: basePrice,
          defaultMethod: defaultPaymentMethod,
          method: selectedPaymentMethod,
          methods: availablePaymentMethods?.reduce(
            (acc, method) => ({ ...acc, [method.bmCode]: method }),
            {},
          ),
          enabledMethods: availablePaymentMethods?.reduce<PaymentMethodCode[]>(
            (acc, method) =>
              isPaymentMethodEnabled(method) ? [...acc, method.bmCode] : acc,
            [],
          ),
          required,
          isExpressCheckout,
          ...(duration !== null ? { duration } : {}),
          ...data,
        },
        type,
      }

      if (type in PaymentMarkName) {
        performance.mark(PaymentMarkName[type as keyof typeof PaymentMarkName])
      }

      logger.info(`[PAYMENT] ${message}`, {
        ...payload,
        owners: ['bot-squad-payin-front'],
      })

      if (FF_NOODLE_PAYMENT_EVENTS_ENABLED && NOODLE_EVENTS.includes(type)) {
        // TODO: fix EventData type https://backmarket.atlassian.net/browse/FRONT-953
        trackPaymentEvent(payload as EventData)
      }
    }

  const track = (
    type: PaymentLogTypes,
    message: string,
    data?: Record<string, unknown>,
  ) => trackBuilder(type, message)(data)

  // Create shortcuts when possible to reduce the boilerplate in the PaymentForm
  track.formDisplayed = trackBuilder(
    'PAYMENT_FORM_DISPLAYED',
    'Payment form displayed',
  )
  track.setupStart = trackBuilder(
    'PAYMENT_FORM_SETUP_START',
    'Form setup start',
  )
  track.setupSuccess = trackBuilder(
    'PAYMENT_FORM_SETUP_SUCCESS',
    'Form setup success',
  )
  track.setupError = trackBuilder(
    'PAYMENT_FORM_SETUP_ERROR',
    'Form setup error',
  )
  track.submitStart = trackBuilder(
    'PAYMENT_FORM_SUBMIT_START',
    'Form submit start',
  )
  track.submitSuccess = trackBuilder(
    'PAYMENT_FORM_SUBMIT_SUCCESS',
    'Form submit success',
  )
  track.submitError = trackBuilder(
    'PAYMENT_FORM_SUBMIT_ERROR',
    'Form submit error',
  )
  track.resumeStart = trackBuilder(
    'PAYMENT_FORM_RESUME_START',
    'Form resume start',
  )
  track.resumeSuccess = trackBuilder(
    'PAYMENT_FORM_RESUME_SUCCESS',
    'Form resume success',
  )
  track.resumeError = trackBuilder(
    'PAYMENT_FORM_RESUME_ERROR',
    'Form resume error',
  )

  return track
}

export type UsePaymentFormEventTracker = ReturnType<
  typeof usePaymentFormEventTracker
>
